/*
 * File: addressesApi.ts
 * Project: rosita
 * -----
 * Last Modified: 24 Feb 2022 11:31:18 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import lodash from 'lodash';
import { ReduxState } from 'redux/store';
import { GetAddressesParams, GetAddressesResponse } from './models/addresses';

/* ******************** Base Query******************** */
const baseUrl = process.env.REACT_APP_CHUCKIE_SUE_BASE_URL || '';
const functionsKey = process.env.REACT_APP_API_HOST_KEY_CHUCKIE_SUE || '';

export const addressesBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const businessId = (api.getState() as ReduxState).app.acuityContext?.selectedCustomer.business.id;
  const isApiVersion = typeof args !== 'string' && args.url === '/diagnostics/version';

  if (!businessId && !isApiVersion) {
    return {
      error: {
        status: 400,
        statusText: 'Bad Request',
        data: 'No division ID received'
      }
    };
  }
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = isApiVersion ? args.url : `divisions/${businessId}/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };

  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  })(adjustedArgs, api, extraOptions);
};

export const chuckieSueAddressesApi = createApi({
  reducerPath: 'chuckieSueAddressesApi',
  baseQuery: addressesBaseQuery,
  endpoints: (builder) => ({
    getAddresses: builder.query<GetAddressesResponse, GetAddressesParams>({
      query: (params) => ({
        url: 'addresses',
        params: lodash.pickBy(params, (v) => v !== undefined)
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().chuckieSueAddressesApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          //
        }
      }
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetAddressesQuery, useLazyGetAddressesQuery, useGetVersionQuery } = chuckieSueAddressesApi;
